import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import parse from 'html-react-parser';
import { useWrittenDirectivesContext } from '../context';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { warning } from '_lex/_foundations/phxColors';

const getHighlightedText = (highlights: any, maxLength: number = 90) => {
  if (!highlights?.texts?.length) return '';

  // Find the hit text (the part that matches the search)
  const hitIndex = highlights.texts.findIndex((text: { type: string }) => text.type === 'hit');
  if (hitIndex === -1) return '';

  const hitText = highlights.texts[hitIndex].value;
  const hitTextLength = hitText.length;

  // Calculate remaining length after accounting for hit text and ellipsis
  const remainingLength = maxLength - hitTextLength - 6; // 6 for ellipsis (3) and strong tags (3)
  const perSideLength = Math.floor(remainingLength / 2);

  // Get text before and after the hit
  const beforeText = hitIndex > 0 ? highlights.texts[hitIndex - 1].value : '';
  const afterText = hitIndex < highlights.texts.length - 1 ? highlights.texts[hitIndex + 1].value : '';

  // Truncate the before and after text if needed
  const truncateAtWordBoundary = (text: string, maxLength: number, fromEnd: boolean = false): string => {
    if (text.length <= maxLength) return text;
    if (fromEnd) {
      const truncated = text.substring(text.length - maxLength);
      const firstSpace = truncated.indexOf(' ');
      return firstSpace > 0 ? '...' + truncated.substring(firstSpace + 1) : '...' + truncated;
    } else {
      const truncated = text.substring(0, maxLength);
      const lastSpace = truncated.lastIndexOf(' ');
      return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...';
    }
  };

  const truncatedBefore = beforeText ? truncateAtWordBoundary(beforeText, perSideLength, true) : '';
  const truncatedAfter = afterText ? truncateAtWordBoundary(afterText, perSideLength) : '';

  return `${truncatedBefore}<strong>${hitText}</strong>${truncatedAfter}`;
};

const SearchResults: React.FC = () => {
  const { searchQuery, setSelectedItem, selectedItem } = useWrittenDirectivesContext();
  const navigate = useNavigate();
  const { id: accreditationId, versionId, standardId } = useParams();

  const searchResults = searchQuery.data?.pages.flatMap((page) => page.content) || [];

  if (!searchResults.length) {
    return null;
  }

  return (
    <Stack spacing={0}>
      {searchResults.map((result) => {
        const isSelected = selectedItem && selectedItem.contentUri === result.contentUri;
        return (
          <Box
            key={result.contentUri}
            sx={{
              cursor: 'pointer',
              mb: 2,
              borderRadius: 1,
              backgroundColor: isSelected ? '#E7F3FF' : '#fff',
              '&:hover': { backgroundColor: '#F3F8FE' },
            }}
            onClick={() => setSelectedItem(result)}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid lightgrey',
              }}
            >
              <Box sx={{ p: 2, minWidth: 0, maxWidth: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    variant='body1'
                    color='primary'
                  >
                    {`${result.heading ? result.heading : ''} ${result.title}`}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='textSecondary'
                    sx={{ ml: 1 }}
                  >
                    {`(${result.manualName})`}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='textSecondary'
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      '& strong': {
                        backgroundColor: warning[100],
                        padding: '0 2px',
                        borderRadius: 1,
                      },
                    }}
                  >
                    {parse(getHighlightedText(result.highlights, 90))}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default SearchResults;
